import React, { useEffect } from 'react';
import { Package, TrendingDown, AlertTriangle, RefreshCw, Truck, Home, Battery } from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line
} from 'recharts';
import { useDashboardStore } from '@/stores/dashboardStore';
import { useAutoRefresh } from '@/hooks';
import { Loading, ErrorMessage, Button, SyncControl } from '@/components/common';
import { formatNumber, formatRelativeTime } from '@/utils/formatters';
import { useAuthStore } from '@/stores/authStore';

const COLORS = ['#3b82f6', '#22c55e', '#eab308', '#ef4444', '#8b5cf6'];

export const DashboardPage: React.FC = () => {
  const { stats, isLoading, error, fetchStats } = useDashboardStore();
  const { user } = useAuthStore();

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useAutoRefresh(() => {
    fetchStats();
  }, []);

  if (isLoading && !stats) {
    return <Loading size="lg" text="جاري تحميل الإحصائيات..." />;
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={fetchStats} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-primary">لوحة التحكم</h1>
        <Button
          variant="secondary"
          size="sm"
          onClick={fetchStats}
          isLoading={isLoading}
          className="gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          تحديث
        </Button>
      </div>

      {stats && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm mb-1">إجمالي البطاريات</p>
                  <p className="text-3xl font-bold text-white">
                    {formatNumber(stats.totalBatteries)}
                  </p>
                </div>
                <div className="bg-primary/20 p-3 rounded-lg">
                  <Battery className="h-8 w-8 text-primary" />
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm mb-1">مخزون المستودع</p>
                  <p className="text-3xl font-bold text-white">
                    {formatNumber(stats.warehouseStock)}
                  </p>
                </div>
                <div className="bg-info/20 p-3 rounded-lg">
                  <Home className="h-8 w-8 text-info" />
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm mb-1">مخزون السائقين</p>
                  <p className="text-3xl font-bold text-white">
                    {formatNumber(stats.driverStock)}
                  </p>
                </div>
                <div className="bg-success/20 p-3 rounded-lg">
                  <Truck className="h-8 w-8 text-success" />
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm mb-1">مخزون منخفض</p>
                  <p className="text-3xl font-bold text-warning">
                    {formatNumber(stats.lowStockCount)}
                  </p>
                </div>
                <div className="bg-warning/20 p-3 rounded-lg">
                  <TrendingDown className="h-8 w-8 text-warning" />
                </div>
              </div>
            </div>
          </div>

          {/* Sync Control - Only for Managers */}
          {user?.role === 'manager' && (
            <SyncControl />
          )}

          {/* Charts Section */}
          {stats.charts && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Transactions History */}
              <div className="card lg:col-span-2">
                <h3 className="text-lg font-semibold mb-4 text-white">حركة المخزون (آخر 7 أيام)</h3>
                <div className="h-[300px] w-full" dir="ltr">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={stats.charts.line}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis dataKey="name" stroke="#9ca3af" />
                      <YAxis stroke="#9ca3af" />
                      <RechartsTooltip
                        contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '0.5rem' }}
                        itemStyle={{ color: '#fff' }}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="value"
                        name="عدد العمليات"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        activeDot={{ r: 8 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Stock Distribution */}
              <div className="card">
                <h3 className="text-lg font-semibold mb-4 text-white">توزيع المخزون</h3>
                <div className="h-[300px] w-full" dir="ltr">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={stats.charts.pie}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {stats.charts.pie.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '0.5rem' }}
                        itemStyle={{ color: '#fff' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Top Brands */}
              <div className="card">
                <h3 className="text-lg font-semibold mb-4 text-white">أكثر العلامات التجارية توفراً</h3>
                <div className="h-[300px] w-full" dir="ltr">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.charts.bar} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis type="number" stroke="#9ca3af" />
                      <YAxis dataKey="name" type="category" stroke="#9ca3af" width={100} />
                      <RechartsTooltip
                        contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '0.5rem' }}
                        itemStyle={{ color: '#fff' }}
                      />
                      <Bar dataKey="value" name="الكمية" fill="#22c55e" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Stock Alerts */}
            <div className="card">
              <h3 className="text-lg font-semibold mb-4 text-white flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-danger" />
                تنبيهات المخزون
              </h3>
              <div className="space-y-4">
                 <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                    <span className="text-gray-300">نفذ من المخزون (Out of Stock)</span>
                    <span className="text-danger font-bold text-xl">{stats.outOfStockCount}</span>
                 </div>
                 <div className="flex justify-between items-center p-3 bg-white/5 rounded-lg">
                    <span className="text-gray-300">مخزون منخفض (Low Stock)</span>
                    <span className="text-warning font-bold text-xl">{stats.lowStockCount}</span>
                 </div>
              </div>
              {stats.lastSyncAt && (
                  <p className="text-xs text-gray-500 mt-4 text-left">
                    آخر مزامنة: {formatRelativeTime(stats.lastSyncAt)}
                  </p>
                )}
            </div>

            {/* Stock by Supplier */}
            <div className="card">
              <h3 className="text-lg font-semibold mb-4 text-white flex items-center gap-2">
                <Package className="h-5 w-5 text-info" />
                المخزون حسب المورد
              </h3>
              <div className="space-y-3 overflow-y-auto max-h-[300px]">
                {Object.entries(stats.bySupplier || {}).map(([supplier, count]) => (
                  <div key={supplier} className="flex justify-between items-center p-2 hover:bg-white/5 rounded">
                    <span className="text-gray-300">{supplier}</span>
                    <span className="font-semibold">{formatNumber(count)}</span>
                  </div>
                ))}
                {Object.keys(stats.bySupplier || {}).length === 0 && (
                    <p className="text-gray-500 text-center py-4">لا توجد بيانات</p>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
